// Comprehensive West Virginia family law data based on Custody X Change and official West Virginia sources
const westVirginiaData = {
  'west-virginia': {
    name: 'West Virginia',
    overview: {
      keyPoints: [
        'West Virginia follows the "best interests of the child" standard with 11 specific factors outlined in West Virginia Code § 48-9-206',
        'Joint custody is not presumed - courts make individualized determinations based on evidence and circumstances',
        'West Virginia uses traditional "custody" and "visitation" terminology rather than modern parenting time language',
        'The state requires detailed parenting plans in contested custody cases involving minor children',
        'West Virginia has a strong preference for maintaining meaningful relationships between children and both parents',
        'Relocation with children requires court approval or written consent from the other parent for moves over 100 miles',
        'West Virginia courts emphasize stability and continuity in the child\'s living arrangements'
      ],
      uniqueFeatures: [
        'West Virginia has specific provisions for coal mining families and shift work considerations',
        'The state allows for informal custody agreements that can be formalized by the court',
        'West Virginia recognizes the challenges of rural geography and limited transportation in custody arrangements',
        'The state has specific statutory provisions for grandparent visitation rights',
        'West Virginia allows for modification of custody arrangements based on changed circumstances',
        'The state considers domestic violence history very seriously in custody determinations',
        'West Virginia has specialized family court systems in larger counties'
      ]
    },
    legalRequirements: {
      residency: 'Either parent or the child must have lived in West Virginia for at least 6 months before filing, or West Virginia must be the child\'s home state under UCCJEA',
      filing: 'File in the county where the child has lived for the past 6 months, or where either parent resides',
      paternity: 'Unmarried fathers can establish paternity through voluntary acknowledgment, genetic testing, court order, or presumption of paternity',
      documentation: 'Required documents include petition, summons, financial statement, and proposed parenting plan in contested cases',
      uniqueRules: [
        'West Virginia requires financial disclosure in all family law cases involving children or support',
        'Mediation is encouraged but not mandatory in custody disputes',
        'Guardian ad litem appointment is discretionary based on case circumstances and child\'s best interests',
        'Parent education programs are required in some counties for divorcing parents',
        'West Virginia allows temporary custody orders for emergency situations involving child welfare'
      ]
    },
    custodyTypes: {
      physicalCustody: 'West Virginia uses "physical custody" to determine where the child primarily resides and who provides day-to-day care',
      legalCustody: 'Legal custody involves decision-making authority for major life decisions including education, healthcare, and religious upbringing',
      jointCustody: 'Joint custody (both legal and physical) requires court approval and demonstration that it serves the child\'s best interests',
      visitation: 'Called "visitation rights" - includes specific schedules for the non-custodial parent\'s time with the child',
      specialProvisions: [
        'Supervised visitation may be ordered when child safety or welfare concerns exist',
        'Long-distance visitation arrangements are common due to West Virginia\'s mountainous terrain',
        'Extended summer visitation is standard to accommodate school schedules and work demands',
        'Holiday visitation typically alternates between parents on annual basis',
        'Virtual visitation (video calls) is increasingly recognized for long-distance and shift work situations',
        'Grandparent visitation rights are protected under West Virginia Code § 48-10-501'
      ]
    },
    parentingPlans: {
      required: true,
      elements: [
        'Detailed custody and visitation schedule including regular rotation, weekends, and holidays',
        'Specific holiday and vacation schedules with clear alternating arrangements between parents',
        'Transportation arrangements and responsibility for travel costs between parents\' residences',
        'Communication methods between parents and guidelines for parent-child contact during visitation',
        'Decision-making authority allocation for education, healthcare, religious, and extracurricular matters',
        'Dispute resolution procedures including mediation requirements before court intervention',
        'Relocation provisions and notification requirements for moves affecting the child\'s residence',
        'Any special conditions, restrictions, or accommodations for either parent',
        'Provisions for child care during work hours and right of first refusal',
        'Guidelines for introducing new romantic partners to the children',
        'Emergency contact procedures and decision-making protocols'
      ],
      tips: [
        'Be extremely specific about pickup and drop-off times, locations, and procedures to avoid confusion',
        'Include detailed provisions for West Virginia\'s mountainous terrain and weather conditions affecting travel',
        'Address how to handle missed visitation due to weather, work schedules, or emergencies',
        'Include specific language about travel distances and meeting locations for exchanges in rural areas',
        'Specify how major decisions will be made and communicated between parents',
        'Address the child\'s participation in extracurricular activities and school events',
        'Include technology use guidelines and virtual visitation provisions for shift workers and long-distance situations',
        'Plan for extended summer arrangements and school break schedules'
      ]
    },
    childSupport: {
      guidelines: 'West Virginia uses a modified Income Shares Model based on both parents\' gross income with West Virginia-specific factors',
      factors: [
        'Gross monthly income of both parents from all sources',
        'Number of children covered by the support order',
        'Cost of health insurance premiums for the children',
        'Childcare expenses necessary for employment or education',
        'Extraordinary medical expenses not covered by insurance',
        'Other court-ordered support obligations',
        'Visitation time and overnight stays with paying parent (if significant)',
        'Standard of living the child would have enjoyed if parents remained together',
        'Special needs of the child requiring additional financial support',
        'Travel costs associated with visitation in rural areas'
      ],
      calculation: 'West Virginia Child Support Guidelines using Income Shares Model with state-specific adjustments',
      modifications: 'Substantial change in circumstances required, typically 15% change in support amount or significant change in income or circumstances',
      enforcement: 'Strong enforcement through West Virginia Bureau for Child Support Enforcement including wage garnishment, asset seizure, tax intercept, and license suspension',
      detailedInfo: {
        basicCalculation: {
          description: 'West Virginia follows a modified Income Shares Model that estimates what parents would spend on their children if the family were intact, with adjustments for West Virginia-specific economic factors.',
          steps: [
            'Calculate each parent\'s gross monthly income from all verifiable sources',
            'Determine allowable deductions including taxes, mandatory retirement, and existing support orders',
            'Calculate adjusted gross monthly income for each parent after allowable deductions',
            'Add both parents\' adjusted gross incomes to determine combined monthly income',
            'Use West Virginia Child Support Schedule to find basic support obligation for number of children',
            'Allocate support obligation proportionally based on each parent\'s percentage of combined income',
            'Add costs for health insurance, childcare, and extraordinary medical expenses',
            'Apply any adjustments for extended visitation time or special circumstances',
            'Calculate final monthly support amount payable by obligor parent'
          ]
        },
        incomeTypes: {
          included: [
            'Wages, salary, commissions, bonuses, and overtime pay',
            'Self-employment income (gross receipts minus ordinary business expenses)',
            'Investment income, dividends, interest, and capital gains',
            'Rental property income (after reasonable expenses)',
            'Retirement benefits, pensions, and annuity payments',
            'Social Security benefits (including disability and survivor benefits)',
            'Unemployment compensation and workers\' compensation benefits',
            'Military pay including base pay, allowances, and special pay',
            'Spousal support or maintenance received from previous relationships',
            'Trust income and regularly received gifts or inheritances',
            'Royalties, patents, and intellectual property income',
            'Business income from partnerships, corporations, or LLCs',
            'Coal mining royalties and mineral rights income'
          ],
          excluded: [
            'Means-tested public assistance (TANF, SNAP, WIC, etc.)',
            'Foster care payments received for non-biological children',
            'Child support received for other children not subject to current order',
            'One-time gifts or inheritances (unless creating ongoing income)',
            'Student loans, grants, and educational financial aid',
            'Supplemental Security Income (SSI)',
            'Income of new spouse or cohabitating partner',
            'Reimbursement for job-related expenses',
            'Income from illegal activities',
            'Disaster relief payments'
          ]
        },
        deductions: {
          mandatory: [
            'Federal income taxes based on appropriate filing status',
            'State income taxes (West Virginia state taxes)',
            'FICA taxes (Social Security and Medicare)',
            'Mandatory retirement contributions required by employer',
            'Union dues required as condition of employment',
            'Court-ordered child support for other children',
            'Court-ordered spousal support/maintenance to former spouse'
          ],
          optional: [
            'Health insurance premiums for the child (added separately to support)',
            'Childcare expenses necessary for employment or education',
            'Extraordinary medical expenses for the child (shared proportionally)'
          ]
        },
        visitationAdjustment: {
          description: 'West Virginia allows adjustment to child support when the paying parent has significant overnight visitation time with the child.',
          thresholds: [
            'No adjustment for less than 25% overnight visitation (91 nights per year)',
            'Potential adjustment for 25-35% overnight visitation (91-127 nights per year)',
            'Standard adjustment consideration for 35%+ overnight visitation (128+ nights per year)',
            'Extended visitation adjustment may apply for equal or near-equal time arrangements'
          ],
          calculation: 'Adjustment reduces support obligation based on additional direct costs during extended visitation and reduced costs to custodial parent',
          factors: [
            'Actual overnight stays documented over previous 12 months',
            'Additional housing costs for accommodating child during visitation',
            'Duplicate expenses (clothing, toys, school supplies) in both homes',
            'Transportation costs for visitation exchanges in rural areas',
            'Reduced household expenses for custodial parent during visitation periods'
          ]
        },
        additionalExpenses: {
          healthInsurance: {
            description: 'Cost of health insurance premiums specifically attributable to covering the child',
            allocation: 'Shared proportionally between parents based on their respective income percentages',
            requirements: 'Must be reasonable in cost, accessible to child, and provide adequate coverage',
            considerations: [
              'Premium cost must be specifically for child coverage, not family plan differential',
              'Insurance must be available through employer or affordable marketplace',
              'Coverage should include medical, dental, and vision if reasonably available',
              'Both parents should have access to insurance cards and provider information',
              'Special considerations for rural healthcare access in West Virginia'
            ]
          },
          childcare: {
            description: 'Necessary childcare costs directly related to employment, job training, or education',
            allocation: 'Shared proportionally between parents based on their respective income percentages',
            requirements: 'Must be work-related or education-related necessity for custodial parent',
            considerations: [
              'Childcare must be necessary for parent to work or attend school',
              'Costs should be reasonable for the local area and type of care',
              'Before and after school care is typically included',
              'Summer childcare during school breaks may be included',
              'Receipts and documentation required for verification',
              'Rural childcare availability and costs considered'
            ]
          },
          extraordinaryMedical: {
            description: 'Uninsured medical, dental, and vision expenses exceeding $250 per year per child',
            examples: [
              'Dental treatment not covered by insurance (fillings, crowns, extractions)',
              'Orthodontic treatment and appliances',
              'Vision care including eye exams, glasses, and contact lenses',
              'Mental health counseling and therapy sessions',
              'Physical therapy and rehabilitation services',
              'Prescription medications not covered by insurance',
              'Medical equipment and supplies (braces, wheelchairs, etc.)',
              'Treatment for chronic conditions requiring ongoing care',
              'Emergency medical treatment and hospitalization',
              'Specialized medical care and consultations',
              'Travel expenses for medical care in rural areas'
            ],
            allocation: 'Shared proportionally between parents based on their respective income percentages',
            procedure: 'Parent incurring expense should seek pre-approval for non-emergency expenses over $500'
          },
          extracurricular: {
            description: 'Costs for children\'s activities, sports, and enrichment programs',
            examples: [
              'Youth sports team fees, equipment, and travel costs',
              'Music lessons and instrument rental or purchase',
              'Dance, martial arts, gymnastics, or other activity classes',
              'Summer camps and recreational day programs',
              'School trips, field trips, and educational activities',
              'Tutoring and educational support services',
              'Art, theater, or other creative programs',
              'Religious education and confirmation programs'
            ],
            note: 'Not automatically included in basic support calculation - must be agreed upon by parents or specifically court-ordered',
            considerations: [
              'Activities should be in the child\'s best interest and age-appropriate',
              'Costs should be reasonable and within family\'s financial means',
              'Both parents should have input on major extracurricular decisions',
              'Transportation to activities should be considered, especially in rural areas'
            ]
          }
        },
        modifications: {
          grounds: [
            'Substantial change in either parent\'s income (typically 15% or more)',
            'Significant change in visitation schedule or custody arrangement',
            'Change in health insurance costs or availability',
            'Change in childcare expenses or needs',
            'Child\'s needs change significantly (special needs, medical conditions)',
            'Emancipation, marriage, or death of a child',
            'Three years have passed since last order (allows for cost-of-living review)',
            'Change in number of children covered by order',
            'Job loss or change in employment circumstances'
          ],
          process: [
            'File Petition for Modification of Child Support with supporting documentation',
            'Serve the other parent with petition and financial statement',
            'Provide updated income verification and expense documentation',
            'Attend court hearing or mediation as scheduled by court',
            'Present evidence supporting the requested modification',
            'Obtain modified court order with new support amount'
          ],
          timeline: 'Modifications are typically effective from the date of filing the petition, not the date of the final court order',
          documentation: [
            'Updated financial statement with current income and expenses',
            'Recent pay stubs, tax returns, and employment verification',
            'Documentation of changed circumstances (medical bills, job loss, etc.)',
            'Current childcare and health insurance costs',
            'Evidence of visitation schedule changes if applicable'
          ]
        },
        enforcement: {
          methods: [
            'Income withholding and automatic wage garnishment',
            'Interception of tax refunds (federal and state)',
            'Asset seizure including bank accounts and personal property',
            'Liens on real estate and personal property',
            'Suspension of driver\'s license and professional licenses',
            'Suspension of hunting, fishing, and recreational licenses',
            'Credit bureau reporting of delinquent support',
            'Contempt of court proceedings with possible jail time',
            'Passport denial for arrearages exceeding $2,500',
            'Interception of lottery winnings and gaming proceeds',
            'Garnishment of unemployment and workers\' compensation benefits'
          ],
          interest: 'Unpaid child support accrues interest at the rate of 10% per annum on past-due amounts',
          statute: 'No statute of limitations exists for collecting past-due child support - it remains collectible indefinitely',
          resources: [
            'West Virginia Bureau for Child Support Enforcement',
            'Local prosecuting attorney offices for contempt proceedings',
            'Private collection agencies contracted by the state',
            'Interstate enforcement through other state agencies'
          ]
        },
        calculator: {
          worksheets: [
            'West Virginia Child Support Obligation Worksheet (basic calculation)',
            'Shared Physical Custody Worksheet (for significant visitation time)',
            'Multiple Family Worksheet (when parent has children with multiple partners)',
            'Self-Employment Income Worksheet (for business owners and contractors)'
          ],
          online: 'West Virginia Bureau for Child Support Enforcement provides online calculator tools for estimation purposes',
          accuracy: 'Court calculations may differ from estimates based on specific case circumstances and judicial discretion',
          updates: 'Guidelines are reviewed every four years and updated as necessary to reflect economic changes'
        },
        specialCircumstances: {
          mining: {
            description: 'Special considerations for coal mining and energy industry families',
            factors: [
              'Irregular income from mining operations',
              'Seasonal work patterns in extraction industries',
              'Shift work and extended work rotations',
              'Royalty income from mineral rights',
              'Occupational hazards and disability considerations'
            ]
          },
          rural: {
            description: 'Additional considerations for rural West Virginia families',
            factors: [
              'Limited transportation options and long travel distances',
              'Seasonal employment in agriculture and forestry',
              'Limited childcare and healthcare availability',
              'Economic challenges in rural communities',
              'Extended family support networks'
            ]
          },
          selfEmployed: {
            description: 'Additional requirements for self-employed parents',
            documentation: [
              'Business tax returns for previous two years',
              'Profit and loss statements',
              'Business bank account records',
              'Accounts receivable and payable records',
              'Business expense documentation and receipts'
            ],
            considerations: [
              'Income averaging over multiple years may be used',
              'Personal expenses claimed as business deductions may be added back',
              'Depreciation and other non-cash deductions may be added to income',
              'Court may impute income based on earning capacity if income appears artificially reduced'
            ]
          }
        }
      }
    },
    resources: {
      courts: [
        {
          name: 'West Virginia Judiciary Family Court Resources',
          url: 'http://www.courtswv.gov/lower-courts/family-court.html',
          description: 'Official West Virginia court system family division with forms, procedures, and self-help resources'
        },
        {
          name: 'West Virginia Family Court Forms',
          url: 'http://www.courtswv.gov/legal-community/court-rules/forms.html',
          description: 'Complete collection of West Virginia family court forms for divorce, custody, and support cases'
        },
        {
          name: 'West Virginia Self-Represented Litigant Resources',
          url: 'http://www.courtswv.gov/public-resources/self-help.html',
          description: 'Self-help resources and assistance for people representing themselves in family court'
        },
        {
          name: 'Kanawha County Family Court',
          address: '111 Court Street, Charleston, WV 25301',
          phone: '(304) 357-0130',
          description: 'Family court serving Charleston area and Kanawha County'
        },
        {
          name: 'Monongalia County Family Court',
          address: '243 High Street, Morgantown, WV 26505',
          phone: '(304) 291-7230',
          description: 'Family court serving Morgantown area and Monongalia County'
        },
        {
          name: 'Cabell County Family Court',
          address: '750 Fifth Avenue, Huntington, WV 25701',
          phone: '(304) 526-8625',
          description: 'Family court serving Huntington area and Cabell County'
        }
      ],
      support: [
        {
          name: 'West Virginia Bureau for Child Support Enforcement',
          url: 'https://dhhr.wv.gov/bcse/',
          phone: '1-800-249-3778',
          description: 'Official state agency for child support establishment, modification, and enforcement'
        },
        {
          name: 'West Virginia Child Support Payment Center',
          url: 'https://www.wvpayment.com/',
          description: 'Official payment processing and case management portal for West Virginia child support'
        },
        {
          name: 'Legal Aid of West Virginia',
          url: 'https://legalaidwv.org/',
          phone: '1-800-642-8279',
          description: 'Free legal services for low-income families in family law matters statewide'
        },
        {
          name: 'West Virginia State Bar Lawyer Referral Service',
          url: 'https://www.wvbar.org/public-resources/lawyer-referral-service/',
          phone: '(304) 558-2456',
          description: 'Professional attorney referrals for family law cases throughout West Virginia'
        },
        {
          name: 'West Virginia Coalition Against Domestic Violence',
          url: 'https://www.wvcadv.org/',
          phone: '1-304-965-3552',
          description: 'Statewide domestic violence resources, shelters, and legal advocacy services'
        },
        {
          name: 'West Virginia Fathers Rights Support Groups',
          url: 'https://www.facebook.com/groups/wvfathersrights',
          description: 'Support networks and advocacy groups for fathers navigating family court in West Virginia'
        }
      ],
      legal: [
        {
          name: 'West Virginia State Bar Association Family Law Section',
          url: 'https://www.wvbar.org/sections/family-law/',
          description: 'Professional resources and directory of family law attorneys in West Virginia'
        },
        {
          name: 'West Virginia University College of Law Legal Clinic',
          url: 'https://law.wvu.edu/academics/clinics-externships',
          description: 'Student-supervised legal services for qualifying family law matters'
        },
        {
          name: 'West Virginia Collaborative Law Professionals',
          url: 'https://www.collaborativelaw.org/find-a-professional/west-virginia/',
          description: 'Network of collaborative divorce attorneys and professionals in West Virginia'
        },
        {
          name: 'West Virginia Mediation Association',
          url: 'https://www.wvmediation.org/',
          description: 'Family mediation services and dispute resolution professionals statewide'
        },
        {
          name: 'Pro Bono Programs West Virginia',
          url: 'https://www.wvbar.org/public-resources/pro-bono/',
          description: 'Free and reduced-cost legal services for qualifying individuals'
        }
      ],
      educational: [
        {
          name: 'Children First West Virginia',
          url: 'http://www.courtswv.gov/public-resources/family-court/parent-education.html',
          description: 'Parent education program for divorcing parents with minor children (required in some counties)'
        },
        {
          name: 'West Virginia Parenting Plan Resources',
          url: 'http://www.courtswv.gov/public-resources/family-court/parenting-plans.html',
          description: 'Comprehensive guide to creating effective parenting plans in West Virginia'
        },
        {
          name: 'West Virginia Guardian ad Litem Program',
          url: 'http://www.courtswv.gov/public-resources/family-court/guardian-ad-litem.html',
          description: 'Information about guardian ad litem services for children in contested custody cases'
        },
        {
          name: 'West Virginia University Extension - Family Life',
          url: 'https://extension.wvu.edu/families-health',
          description: 'Educational programs for co-parenting, family relationships, and child development'
        },
        {
          name: 'West Virginia Parent Information and Resource Centers',
          url: 'https://wvde.us/special-education/parent-information/',
          description: 'Educational resources for parents navigating special needs and educational decisions'
        }
      ],
      filingInformation: {
        venues: [
          {
            name: 'Kanawha County Circuit Court',
            address: '111 Court Street, Charleston, WV 25301',
            phone: '(304) 357-0130',
            jurisdiction: 'Kanawha County - Charleston, South Charleston, Dunbar, and surrounding areas'
          },
          {
            name: 'Monongalia County Circuit Court',
            address: '243 High Street, Morgantown, WV 26505',
            phone: '(304) 291-7230',
            jurisdiction: 'Monongalia County - Morgantown, Westover, Star City, and surrounding areas'
          },
          {
            name: 'Cabell County Circuit Court',
            address: '750 Fifth Avenue, Huntington, WV 25701',
            phone: '(304) 526-8625',
            jurisdiction: 'Cabell County - Huntington, Barboursville, and surrounding areas'
          },
          {
            name: 'Jefferson County Circuit Court',
            address: '100 West Washington Street, Charles Town, WV 25414',
            phone: '(304) 728-3213',
            jurisdiction: 'Jefferson County - Charles Town, Shepherdstown, and eastern panhandle'
          },
          {
            name: 'Wood County Circuit Court',
            address: '1 Court Square, Parkersburg, WV 26101',
            phone: '(304) 424-1850',
            jurisdiction: 'Wood County - Parkersburg, Vienna, and surrounding areas'
          },
          {
            name: 'Raleigh County Circuit Court',
            address: '215 Main Street, Beckley, WV 25801',
            phone: '(304) 255-9321',
            jurisdiction: 'Raleigh County - Beckley, Oak Hill, and southern West Virginia'
          }
        ],
        filingFees: {
          'Divorce (with children)': '$135.00',
          'Divorce (without children)': '$135.00',
          'Custody/Visitation': '$135.00',
          'Child Support': '$135.00',
          'Modification': '$135.00',
          'Paternity': '$135.00',
          'Protection Order': 'No fee'
        },
        requiredForms: [
          'Petition for Divorce or Custody',
          'Summons',
          'Financial Statement',
          'Parenting Plan (if children involved)',
          'Child Support Worksheets',
          'Certificate of Service',
          'Final Decree of Divorce or Custody Order'
        ]
      },
      statutes: [
        {
          name: 'West Virginia Code Chapter 48 - Domestic Relations',
          url: 'http://www.wvlegislature.gov/wvcode/chapterentire.cfm?chap=48',
          description: 'Primary statutes governing divorce, custody, and family law in West Virginia'
        },
        {
          name: 'West Virginia Child Support Guidelines (W.Va. Code § 48-13-301 et seq.)',
          url: 'http://www.wvlegislature.gov/wvcode/ChapterEntire.cfm?chap=48&art=13',
          description: 'Official child support calculation guidelines and procedures'
        },
        {
          name: 'West Virginia Uniform Child Custody Jurisdiction Act (W.Va. Code § 48-20-101 et seq.)',
          url: 'http://www.wvlegislature.gov/wvcode/ChapterEntire.cfm?chap=48&art=20',
          description: 'Interstate custody jurisdiction and enforcement laws'
        },
        {
          name: 'West Virginia Parental Rights and Responsibilities (W.Va. Code § 48-9-101 et seq.)',
          url: 'http://www.wvlegislature.gov/wvcode/ChapterEntire.cfm?chap=48&art=9',
          description: 'Custody determination factors and best interests standards'
        }
      ],
      emergencyContacts: [
        {
          name: 'West Virginia Domestic Violence Hotline',
          phone: '1-800-352-6513',
          description: '24/7 statewide domestic violence crisis line and support services'
        },
        {
          name: 'West Virginia Child Abuse Hotline',
          phone: '1-800-352-6513',
          description: '24/7 child abuse and neglect reporting hotline'
        },
        {
          name: 'West Virginia Crisis Intervention',
          phone: '211',
          description: 'Dial 211 for mental health crisis intervention and counseling referrals'
        },
        {
          name: 'Legal Aid of West Virginia Emergency Line',
          phone: '1-800-642-8279',
          description: 'Emergency legal assistance hotline for urgent family law matters'
        }
      ]
    },
    bestInterestsFactors: [
      'The care-taking functions each parent has performed and will perform in the future on behalf of the child',
      'The disposition of each parent to support the child\'s relationship with the other parent',
      'The relationship between the child and each parent',
      'The reasonable preferences of the child, if the court deems the child to be of sufficient maturity',
      'The willingness and ability of each parent to facilitate and encourage continuing contact and a close relationship between the child and the other parent',
      'The geographical proximity of the parents to each other as this relates to the practical considerations of where the child will reside',
      'The current physical, mental, and moral fitness of each parent',
      'Whether either parent has been convicted of child abuse or neglect',
      'Whether domestic violence or the threat of domestic violence as defined in section four hundred one, article twenty-seven of this chapter has occurred',
      'Whether either parent has been convicted of a felony involving violence or abuse',
      'Such other factors as the court deems necessary to consider in order to determine what is in the best interests of the child'
    ],
    practicalTips: [
      'West Virginia courts value stability and consistency - document your regular involvement in your child\'s life',
      'Keep detailed records of all visitation time, communications with the other parent, and child-related expenses',
      'Be prepared for potential long-distance travel due to West Virginia\'s mountainous terrain and rural nature',
      'Weather and road conditions can significantly impact visitation - include flexible provisions in your parenting plan',
      'West Virginia courts appreciate parents who can demonstrate cooperation and effective communication',
      'Consider hiring a local attorney familiar with West Virginia judges and court procedures',
      'Take advantage of extended summer visitation opportunities to maximize time with your children',
      'Document any special circumstances related to shift work, mining schedules, or rural living conditions'
    ],
    processingInformation: {
      mediation: {
        description: 'West Virginia encourages mediation to resolve family law disputes before trial',
        benefits: [
          'Less expensive than going to trial',
          'Faster resolution of disputes',
          'More control over the outcome',
          'Less adversarial than court proceedings',
          'Confidential process that protects privacy',
          'Can preserve co-parenting relationships'
        ],
        process: [
          'Either party can request mediation or court may suggest it',
          'Both parties must agree to participate in good faith',
          'Mediator helps facilitate discussion and negotiation',
          'Any agreements reached are put in writing',
          'Agreements can be submitted to court for approval',
          'If mediation fails, case proceeds to trial'
        ],
        cost: 'Typically ranges from $75-200 per hour, often split between parties'
      },
      parentEducation: {
        description: 'Some West Virginia counties require parent education programs for divorcing parents',
        purpose: [
          'Help parents understand the impact of divorce on children',
          'Provide information about child development and co-parenting',
          'Discuss alternatives to litigation',
          'Encourage settlement and cooperation',
          'Identify resources for families in transition'
        ],
        requirements: [
          'Required in some counties for cases involving minor children',
          'Must be completed within specified timeframe',
          'Sessions typically last 4-6 hours',
          'Cost varies by county and provider',
          'Some programs offer sliding scale fees'
        ]
      },
      settlement: {
        description: 'West Virginia encourages settlement through various alternative dispute resolution methods',
        options: [
          'Mediation with trained family mediators',
          'Collaborative law with specially trained attorneys',
          'Settlement conferences with judges',
          'Attorney-to-attorney negotiations',
          'Informal negotiations between parties'
        ],
        advantages: [
          'Faster resolution than trial',
          'Lower cost than litigation',
          'More privacy and confidentiality',
          'Greater control over outcomes',
          'Less stress and trauma for children',
          'Preserves co-parenting relationships'
        ]
      },
      trial: {
        description: 'If settlement cannot be reached, West Virginia family court cases proceed to trial',
        process: [
          'Pre-trial conferences to narrow issues',
          'Discovery process to gather evidence',
          'Guardian ad litem investigation if appointed',
          'Trial before family court judge',
          'Presentation of evidence and testimony',
          'Final orders and judgment entered'
        ],
        timeline: 'Trials typically occur 6-18 months after filing, depending on court calendar and case complexity',
        preparation: [
          'Gather all relevant financial documents',
          'Prepare witness lists and evidence',
          'Complete court-ordered evaluations or studies',
          'Attend required mediation or parent education',
          'Work with attorney to develop case strategy'
        ]
      }
    }
  }
};

export default westVirginiaData;